import { useCallback, useEffect, useState } from 'react';

import { Box, Group, Radio, SimpleGrid } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { useTranslation } from 'react-i18next';

import {
  DECIMAL_PROPS,
  InputLabel,
  NumberInput,
  Select,
  TextInput,
  YesNoRadioButton,
} from 'components';
import { CreditOptionsResponse, CreditRequestForm, CreditResponse, HousingExpense } from 'types';

import CreditRequestExpenses from './expenses';

interface CreditRequestOutcomesProps {
  credit: CreditResponse;
  form: UseFormReturnType<CreditRequestForm>;
  visibleFields: string[];
  options: CreditOptionsResponse;
  readOnly?: boolean;
}

const CreditRequestOutcomes = ({
  credit,
  form,
  visibleFields,
  options,
  readOnly = false,
}: CreditRequestOutcomesProps) => {
  const { t } = useTranslation();

  const getInitialHousingExpense = (): HousingExpense | undefined => {
    if (credit.mortgage) return 'MORTGAGE';
    if (credit.rent) return 'RENT';
    return undefined;
  };

  const [housingExpense, setHousingExpense] = useState<HousingExpense | undefined>(getInitialHousingExpense);

  const changeHousingExpense = useCallback(
    (val: HousingExpense) => {
      setHousingExpense(val);
      if (val === "MORTGAGE") {
        form.setFieldValue("mortgage", true);
        form.setFieldValue("rent", undefined);
      } else if (val === "RENT") {
        form.setFieldValue("rent", true);
        form.setFieldValue("mortgage", undefined);
      }
    },
    [form],
  );

  useEffect(() => {
    const formValues = form.getValues();
    if (Object.keys(formValues).length === 0) {
      return;
    }
    if (formValues.additionalExpenses === false) {
      //not using visibleFields here because expenses is an array
      form.setFieldValue('expenses', []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.getValues().additionalExpenses]);

  return (
    <Box p={'md'}>
      <Box>
        <SimpleGrid cols={4}>
          <Select
            label={t('credit.houseSituation.label')}
            data={options['houseSituation'].map((el) => ({
              value: el,
              label: t(`credit.houseSituation.options.${el}`),
            }))}
            key={form.key('houseSituation')}
            {...form.getInputProps('houseSituation')}
            clearable={true}
            disabled={readOnly}
          />
          <Box>
            <InputLabel label={t('credit.mortgageOrRent.label')} />
            <Group>
              <Radio
                checked={housingExpense === 'MORTGAGE'}
                onChange={() => changeHousingExpense('MORTGAGE')}
                label={t('credit.mortgageOrRent.options.MORTGAGE')}
                disabled={readOnly}
              />
              <Radio
                checked={housingExpense === 'RENT'}
                onChange={() => changeHousingExpense('RENT')}
                label={t('credit.mortgageOrRent.options.RENT')}
                disabled={readOnly}
              />
            </Group>
          </Box>
          {visibleFields.includes("mortgageAmount") && (
            <NumberInput
              label={t('credit.mortgageAmount.label')}
              key={form.key('mortgageAmount')}
              {...form.getInputProps('mortgageAmount')}
              min={0}
              {...DECIMAL_PROPS}
              disabled={readOnly}
            />
          )}
          {visibleFields.includes("rentAmount") && (
            <NumberInput
              label={t('credit.rentAmount.label')}
              key={form.key('rentAmount')}
              {...form.getInputProps('rentAmount')}
              min={options['rentAmount']?.min}
              max={options['rentAmount']?.max}
              {...DECIMAL_PROPS}
              disabled={readOnly}
            />
          )}
        </SimpleGrid>
      </Box>

      <Box mt={'sm'}>
        <SimpleGrid cols={4}>
          <NumberInput
            label={t('credit.healthInsuranceAmount.label')}
            key={form.key('healthInsuranceAmount')}
            {...form.getInputProps('healthInsuranceAmount')}
            min={0}
            {...DECIMAL_PROPS}
            disabled={readOnly}
          />
          <YesNoRadioButton
            label={t('credit.healthInsuranceDiscount.label')}
            name={'healthInsuranceDiscount'}
            form={form}
            disabled={readOnly}
          />
          <YesNoRadioButton
            label={t('credit.enforcedCollection.label')}
            name={'enforcedCollection'}
            form={form}
            disabled={readOnly}
          />
          <YesNoRadioButton
            label={t(`credit.expenseType.anotherExpense.label`)}
            name={'additionalExpenses'}
            form={form}
            disabled={readOnly}
          />
        </SimpleGrid>
      </Box>

      {form.getValues().additionalExpenses && (
        <CreditRequestExpenses credit={credit} form={form} options={options} readOnly={readOnly} />
      )}

      <Box mt={'sm'}>
        <SimpleGrid cols={4}>
          <TextInput
            label={t('credit.comment.singular')}
            key={form.key('comment4')}
            {...form.getInputProps('comment4')}
            disabled={readOnly}
          />
        </SimpleGrid>
      </Box>
    </Box>
  );
};

export default CreditRequestOutcomes;
